import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import { useOrganization } from '@/contexts/OrganizationContext';
import { cn } from '@/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';

import {
  quickAddColumns,
  quickAddItems,
  type QuickAddColumn,
  type QuickAddDialogAction,
  type QuickAddItem,
} from '@/config/quickAddItems';
import { isNavItemVisible, useNavVisibilityContext } from '@/config/navVisibility';

import { useLiftedFormDialogState } from '@/hooks/useLiftedFormDialogState';
import { CreateExpenseForm } from '@/components/accounting/CreateExpenseForm';
import { CreateARInvoiceForm } from '@/components/accounting/CreateARInvoiceForm';
import { CreateAPInvoiceForm } from '@/components/accounting/CreateAPInvoiceForm';
import { CreateOrUpdateAccountModal } from '@/components/accounting/CreateOrUpdateAccountModal';
import { CreateOrUpdateTransactionModal } from '@/components/accounting/CreateTransactionModal';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { CreateAssetDialog } from '@/components/assets/CreateAssetDialog';
import { PersonCreateDialog } from '@/components/people/PersonCreateDialog';
import { CreateBusinessDialog } from '@/components/business/CreateBusinessDialog';
import { CreatePlaceDialog } from '@/components/places/CreatePlaceDialog';
import { UploadFileDialog } from '@/components/CreateFileDialog';
import { CreateMemoDialog } from '@/components/memos/CreateMemoDialog';
import { SurveyFormDialog } from '@/components/surveys/SurveyFormDialog';
import { surveyDetailPath } from '@/components/surveys/surveyLabels';
import { CreateTimesheetDialog } from '@/components/timesheets/CreateTimesheetDialog';
import { CreateSustainabilityActivityDialog } from '@/components/sustainability/CreateSustainabilityActivityDialog';
import { CreateBudgetDialog } from '@/components/budgets/CreateBudgetDialog';

interface QuickAddPanelProps {
  /** Trigger element rendered as the popover anchor. */
  children: React.ReactNode;
}

const COLUMN_ACCENT: Record<QuickAddColumn, string> = {
  accounting: 'text-releve-teal',
  resources: 'text-emerald-700 dark:text-emerald-400',
  other: 'text-muted-foreground',
};

export function QuickAddPanel({ children }: QuickAddPanelProps) {
  const { t } = useTranslation('common');
  const orgNavigate = useOrgNavigate();
  const { selectedOrganization } = useOrganization();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<QuickAddDialogAction | null>(null);
  // Submitting/dirty state lifted from the expense form so a pristine quick-add
  // closes without the "Discard changes?" prompt and the footer disables while
  // the create is in flight.
  const {
    isSubmitting: expenseFormSubmitting,
    isDirty: expenseFormDirty,
    onSubmittingChange: setExpenseFormSubmitting,
    onDirtyChange: setExpenseFormDirty,
    reset: resetExpenseFormState,
  } = useLiftedFormDialogState();
  // "Make this recurring" ticked in the expense / invoice / bill form → the
  // footer saves a schedule, not a document (the journal modal owns its own footer).
  const [isRecurring, setIsRecurring] = useState(false);
  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setIsRecurring(false);
    resetExpenseFormState();
  }, [resetExpenseFormState]);

  // Handlers shared by Expense / AR / AP fullscreen forms
  const [expenseSubmitHandler, setExpenseSubmitHandler] = useState<(() => void) | null>(null);
  const [arSubmitHandler, setArSubmitHandler] = useState<(() => void) | null>(null);
  const [apSubmitHandler, setApSubmitHandler] = useState<(() => void) | null>(null);

  // The same visibility rule as the sidebar (docs/NAVIGATION.md "Visibility
  // gates") — a project manager gets no Accounting column and no budget.
  const navVisibility = useNavVisibilityContext();
  const itemsByColumn = useMemo(() => {
    const grouped: Record<QuickAddColumn, QuickAddItem[]> = {
      accounting: [],
      resources: [],
      other: [],
    };
    for (const item of quickAddItems) {
      if (isNavItemVisible(item, navVisibility)) grouped[item.column].push(item);
    }
    return grouped;
  }, [navVisibility]);
  const visibleColumns = useMemo(
    () => quickAddColumns.filter((column) => itemsByColumn[column.id].length > 0),
    [itemsByColumn],
  );

  const handleItemClick = useCallback(
    (item: QuickAddItem) => {
      setPopoverOpen(false);
      if (item.kind === 'navigate') {
        orgNavigate(item.href);
        return;
      }
      setActiveDialog(item.id);
    },
    [orgNavigate]
  );

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={8}
          className={cn('p-0', visibleColumns.length === 3 ? 'w-[640px]' : 'w-[440px]')}
        >
          <div className="px-4 pt-4 pb-2 border-b">
            <h2 className="text-sm font-semibold">{t('nav.quickAdd.title')}</h2>
          </div>
          <div className={cn('grid gap-2 p-2', visibleColumns.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
            {visibleColumns.map((column) => (
              <div key={column.id} className="flex flex-col gap-1">
                <h3
                  className={cn(
                    'text-[11px] font-semibold uppercase tracking-wide px-2 pt-2 pb-1',
                    COLUMN_ACCENT[column.id]
                  )}
                >
                  {t(column.labelKey)}
                </h3>
                <ul className="flex flex-col">
                  {itemsByColumn[column.id].map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => handleItemClick(item)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="truncate">{t(item.labelKey)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Sub-dialogs — only mount when an org is selected and that dialog is active */}
      {selectedOrganization && (
        <>
          {activeDialog === 'expense' && (
            <FullscreenFormDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              title={t('expenses.recordExpense')}
              submitLabel={isRecurring ? t('recurring.form.submit.create') : t('expenses.recordExpenseSubmit')}
              onSubmit={(e) => {
                e.preventDefault();
                expenseSubmitHandler?.();
              }}
              formId="quick-add-expense-form"
              isSubmitting={expenseFormSubmitting}
              skipCloseConfirmation={!expenseFormDirty}
            >
              <CreateExpenseForm
                onSuccess={closeDialog}
                onSubmitReady={(handler) => setExpenseSubmitHandler(() => handler)}
                onSubmittingChange={setExpenseFormSubmitting}
                onDirtyChange={setExpenseFormDirty}
                allowRecurring
                onRecurringChange={setIsRecurring}
              />
            </FullscreenFormDialog>
          )}

          {activeDialog === 'invoice' && (
            <FullscreenFormDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              title={t('invoices.createInvoice')}
              submitLabel={isRecurring ? t('recurring.form.submit.create') : t('invoices.createInvoice')}
              onSubmit={(e) => {
                e.preventDefault();
                arSubmitHandler?.();
              }}
              formId="quick-add-ar-invoice-form"
            >
              <CreateARInvoiceForm
                onSuccess={closeDialog}
                onSubmitReady={(handler) => setArSubmitHandler(() => handler)}
                allowRecurring
                onRecurringChange={setIsRecurring}
              />
            </FullscreenFormDialog>
          )}

          {activeDialog === 'bill' && (
            <FullscreenFormDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              title={t('billing.createBill')}
              submitLabel={isRecurring ? t('recurring.form.submit.create') : t('billing.createBill')}
              onSubmit={(e) => {
                e.preventDefault();
                apSubmitHandler?.();
              }}
              formId="quick-add-ap-invoice-form"
            >
              <CreateAPInvoiceForm
                onSuccess={closeDialog}
                onSubmitReady={(handler) => setApSubmitHandler(() => handler)}
                allowRecurring
                onRecurringChange={setIsRecurring}
              />
            </FullscreenFormDialog>
          )}

          {activeDialog === 'account' && (
            <CreateOrUpdateAccountModal
              organizationId={selectedOrganization.id}
              open
              onOpenChange={(o) => !o && closeDialog()}
            />
          )}

          {activeDialog === 'journalEntry' && (
            <CreateOrUpdateTransactionModal
              organizationId={selectedOrganization.id}
              open
              onOpenChange={(o) => !o && closeDialog()}
              allowRecurring
            />
          )}

          {activeDialog === 'project' && (
            <CreateProjectDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onProjectCreated={closeDialog}
            />
          )}

          {activeDialog === 'asset' && (
            <CreateAssetDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onAssetCreated={closeDialog}
            />
          )}

          {activeDialog === 'person' && (
            <PersonCreateDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onSuccess={closeDialog}
            />
          )}

          {activeDialog === 'business' && (
            <CreateBusinessDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onBusinessCreated={closeDialog}
            />
          )}

          {/* Repointed in Phase 5 (review finding W10): the old
              CreateLocationWithMapDialog call site was missing its required
              props and crashed on open; places are the go-forward model. */}
          {activeDialog === 'location' && (
            <CreatePlaceDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onPlaceCreated={closeDialog}
            />
          )}

          {activeDialog === 'file' && (
            <UploadFileDialog
              onOpenChange={(o) => !o && closeDialog()}
            />
          )}

          {activeDialog === 'memo' && (
            <CreateMemoDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
            />
          )}

          {activeDialog === 'timesheet' && (
            <CreateTimesheetDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
            />
          )}

          {activeDialog === 'activity' && (
            <CreateSustainabilityActivityDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onActivityCreated={closeDialog}
              navigateOnSuccess
            />
          )}

          {activeDialog === 'survey' && (
            <SurveyFormDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onSaved={(result) => {
                closeDialog();
                orgNavigate(surveyDetailPath(result.survey.code));
              }}
            />
          )}

          {activeDialog === 'budget' && (
            <CreateBudgetDialog
              open
              onOpenChange={(o) => !o && closeDialog()}
              onCreated={closeDialog}
            />
          )}
        </>
      )}
    </>
  );
}
